/**
 * EagleEye · Live map of every actor in the tenant.
 *
 * Superadmin-only. Aggregates the last-known GPS of office staff, field
 * staff, gov users, customers, service partners and in-flight ambulances
 * and renders them as coloured markers on a Leaflet map. Top-bar chips
 * filter which groups are visible. Selecting a marker opens a side
 * panel with actor details and a breadcrumb polyline of the last 30
 * points from `/api/admin/eagleeye/track/{group}:{id}`.
 *
 * Polls `/api/admin/eagleeye/live` every 15s so the operator watches
 * movement in near real-time without WebSockets.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet.heat";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Buildings, MapTrifold, ArrowsClockwise, X, User, Ambulance,
  ShieldCheck, Storefront, HeartStraight, Briefcase, Fire,
  ClockCounterClockwise, Play, Pause, FileText, Sun, Moon, Link as LinkIcon,
} from "@phosphor-icons/react";

// Base marker fallback
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// ── Filter groups with colour + icon ────────────────────────────────
const GROUPS = [
  { key: "office",    label: "Office",    color: "#0F766E", Icon: Briefcase },
  { key: "field",     label: "Field",     color: "#B45309", Icon: ShieldCheck },
  { key: "gov",       label: "Gov",       color: "#4338CA", Icon: Buildings },
  { key: "customer",  label: "Customers", color: "#DC2626", Icon: HeartStraight },
  { key: "partner",   label: "Partners",  color: "#166534", Icon: Storefront },
  { key: "ambulance", label: "Ambulance", color: "#B91C1C", Icon: Ambulance },
];
const GROUP_META = Object.fromEntries(GROUPS.map(g => [g.key, g]));

// Coloured DivIcon per group with a live-pulse when moving
const makeIcon = (color, moving = false) => new L.DivIcon({
  className: "",
  html: `
    <div style="position:relative;width:26px;height:26px;">
      ${moving ? `
        <span style="position:absolute;inset:0;border-radius:50%;
                      background:${color}55;animation:eagle-pulse 1.4s ease-out infinite;"></span>` : ""}
      <div style="position:relative;width:26px;height:26px;background:${color};
                   border:3px solid white;border-radius:50%;
                   box-shadow:0 4px 10px ${color}77;"></div>
    </div>
    <style>@keyframes eagle-pulse{0%{transform:scale(1);opacity:.75}100%{transform:scale(2.4);opacity:0}}</style>`,
  iconSize: [26, 26], iconAnchor: [13, 13],
});

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points?.length) return;
    const bounds = L.latLngBounds(points.map(p => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
  }, [points, map]);
  return null;
}

// Leaflet-heat wrapper — attaches / re-attaches the heat layer as the
// underlying points change. Intensity is proportional to group weight
// (customers/field pinged frequently, partners are static bases).
function HeatmapLayer({ points, weightFn }) {
  const map = useMap();
  const layerRef = useRef(null);
  useEffect(() => {
    if (!points?.length) {
      if (layerRef.current) { map.removeLayer(layerRef.current); layerRef.current = null; }
      return;
    }
    const data = points.map(p => [p.lat, p.lng, weightFn ? weightFn(p) : 0.5]);
    if (!layerRef.current) {
      layerRef.current = L.heatLayer(data, {
        radius: 30, blur: 22, maxZoom: 14, minOpacity: 0.35,
        gradient: {
          0.2: "#22d3ee", 0.4: "#4ade80",
          0.6: "#fbbf24", 0.8: "#f97316", 1.0: "#dc2626",
        },
      }).addTo(map);
    } else {
      layerRef.current.setLatLngs(data);
    }
    return () => {
      if (layerRef.current) { map.removeLayer(layerRef.current); layerRef.current = null; }
    };
  }, [points, weightFn, map]);
  return null;
}

const FALLBACK = { lat: 10.8505, lng: 76.2711 };   // Kerala centroid


export default function EagleEye() {
  const [feed, setFeed]         = useState({ people: [], counts: {} });
  const [loading, setLoading]   = useState(true);
  const [visible, setVisible]   = useState(new Set(GROUPS.map(g => g.key)));
  const [selected, setSelected] = useState(null);
  const [track, setTrack]       = useState([]);
  const [lastPos, setLastPos]   = useState({});   // detect movement
  const [heatOn, setHeatOn]     = useState(false);
  const [replay, setReplay]     = useState(null);
  const [replayIdx, setReplayIdx] = useState(0);
  const [replayPlaying, setReplayPlaying] = useState(false);
  // Iter 155 — extras
  const [theme, setTheme]                 = useState("dark");   // dark | light
  const [partnerKinds, setPartnerKinds]   = useState(new Set()); // subtype filter
  const [reportOpen, setReportOpen]       = useState(false);
  // Explicit replay window (null → last-24h default)
  const [replayFrom, setReplayFrom] = useState("");
  const [replayTo, setReplayTo]     = useState("");
  const timer = useRef(null);
  const playTimer = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/eagleeye/live", { params: { since_hours: 24 } });
      setFeed(r.data);
      // Diff previous positions so markers pulse when moved.
      setLastPos(prev => {
        const next = {};
        for (const p of r.data.people || []) {
          const key = p.id;
          const before = prev[key];
          const moved = before && (before.lat !== p.lat || before.lng !== p.lng);
          next[key] = { lat: p.lat, lng: p.lng, moved };
        }
        return next;
      });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    timer.current = setInterval(load, 15000);
    return () => { if (timer.current) clearInterval(timer.current); };
  }, []);

  // Iter 155 — hydrate replay state from URL bookmark on first mount.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const from = p.get("from");
    const to = p.get("to");
    const idx = p.get("frame");
    if (from && to) {
      setReplayFrom(from.slice(0, 16));
      setReplayTo(to.slice(0, 16));
      (async () => {
        try {
          const r = await api.get("/admin/eagleeye/replay", {
            params: { from_iso: from, to_iso: to, step_minutes: 15 },
          });
          setReplay(r.data);
          setReplayIdx(Math.min(Number(idx || 0), r.data.frames.length - 1));
        } catch { /* silent */ }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyBookmark = async () => {
    if (!replay) {
      toast.message("Start Replay first to bookmark a moment");
      return;
    }
    const u = new URL(window.location.href);
    u.searchParams.set("from", replay.start);
    u.searchParams.set("to", replay.end);
    u.searchParams.set("frame", String(replayIdx));
    try {
      await navigator.clipboard.writeText(u.toString());
      toast.success("Replay bookmark copied — paste it anywhere");
    } catch { toast.message(u.toString()); }
  };

  const filtered = useMemo(() => {
    const source = (replay && replay.frames[replayIdx]?.people) || (feed.people || []);
    return source.filter(p => {
      if (!visible.has(p.group)) return false;
      // Iter 155 — partner subtype filter (only applied when partners
      // are visible AND the user has picked at least one kind).
      if (p.group === "partner" && partnerKinds.size > 0) {
        const kinds = (p.meta?.kinds) || [];
        if (!kinds.some(k => partnerKinds.has(k))) return false;
      }
      return true;
    });
  }, [feed.people, visible, replay, replayIdx, partnerKinds]);

  // Union of all partner kinds seen in the feed — used to render subtype chips.
  const availablePartnerKinds = useMemo(() => {
    const s = new Set();
    (feed.people || []).forEach(p => {
      if (p.group === "partner") (p.meta?.kinds || []).forEach(k => s.add(k));
    });
    return Array.from(s).sort();
  }, [feed.people]);

  const startReplay = async () => {
    try {
      const params = replayFrom && replayTo
        ? { from_iso: new Date(replayFrom).toISOString(),
            to_iso:   new Date(replayTo).toISOString(),
            step_minutes: 15 }
        : { since_hours: 24, step_minutes: 15 };
      const r = await api.get("/admin/eagleeye/replay", { params });
      setReplay(r.data);
      setReplayIdx(0);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const stopReplay = () => {
    setReplay(null);
    setReplayPlaying(false);
    if (playTimer.current) { clearInterval(playTimer.current); playTimer.current = null; }
  };

  useEffect(() => {
    if (!replay || !replayPlaying) return;
    playTimer.current = setInterval(() => {
      setReplayIdx((i) => {
        const max = replay.frames.length - 1;
        if (i >= max) { setReplayPlaying(false); return max; }
        return i + 1;
      });
    }, 400);
    return () => { if (playTimer.current) { clearInterval(playTimer.current); playTimer.current = null; } };
  }, [replay, replayPlaying]);

  const openActor = async (p) => {
    setSelected(p);
    setTrack([]);
    try {
      const r = await api.get(`/admin/eagleeye/track/${encodeURIComponent(p.id)}`);
      setTrack(r.data.points || []);
    } catch { /* silent */ }
  };

  const toggleGroup = (k) => setVisible(v => {
    const n = new Set(v);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });

  const center = filtered[0] ? { lat: filtered[0].lat, lng: filtered[0].lng } : FALLBACK;

  return (
    <div className={`flex flex-col h-[calc(100vh-64px)] ${theme === "dark" ? "bg-[#0F172A] text-white" : "bg-[#F1F5F9] text-slate-900"}`}
          data-testid="eagleeye-root">
      {/* ═══ Top bar ═══════════════════════════════════════════════ */}
      <div className={`flex flex-wrap items-center gap-3 px-4 py-2.5 border-b ${theme === "dark" ? "border-white/10 bg-[#0B1220]" : "border-slate-200 bg-white"}`}>
        <div className="flex items-center gap-2">
          <MapTrifold size={20} weight="fill" className="text-emerald-300"/>
          <div>
            <div className="font-heading text-[15px] leading-tight">EagleEye</div>
            <div className="text-[10px] text-white/60 leading-tight">
              Live locations of every actor · updated every 15s
            </div>
          </div>
        </div>

        <div className="flex-1 flex flex-wrap items-center gap-1.5">
          {GROUPS.map(g => {
            const count = feed.counts?.[g.key] || 0;
            const on = visible.has(g.key);
            const Icon = g.Icon;
            return (
              <button
                key={g.key}
                onClick={() => toggleGroup(g.key)}
                data-testid={`eye-filter-${g.key}`}
                className={`h-8 pl-1.5 pr-2.5 rounded-full inline-flex items-center gap-1.5 text-[11px] font-bold border transition ${
                  on
                    ? "text-white border-transparent"
                    : "text-white/40 border-white/15 bg-transparent"
                }`}
                style={{ background: on ? `${g.color}` : undefined }}
              >
                <span
                  className="w-5 h-5 rounded-full flex items-center justify-center"
                  style={{ background: on ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.08)" }}
                >
                  <Icon size={11} weight="fill"/>
                </span>
                {g.label}
                <span className="opacity-80 font-mono text-[10px]">{count}</span>
              </button>
            );
          })}
        </div>

        <button onClick={() => setHeatOn(v => !v)}
                 data-testid="eye-heatmap-toggle"
                 className={`h-8 px-2.5 inline-flex items-center gap-1 rounded-full text-[11px] font-bold transition ${
                   heatOn
                     ? "bg-gradient-to-r from-amber-500 to-red-600 text-white"
                     : "bg-white/10 hover:bg-white/20 text-white"
                 }`}>
          <Fire size={12} weight={heatOn ? "fill" : "regular"}/>
          Heatmap
        </button>

        <button onClick={replay ? stopReplay : () => {
                   // Default to last-24h window in the date picker
                   const now = new Date();
                   const dayAgo = new Date(now.getTime() - 24 * 3600_000);
                   const fmt = (d) => d.toISOString().slice(0, 16);
                   setReplayFrom(fmt(dayAgo));
                   setReplayTo(fmt(now));
                 }}
                 data-testid="eye-replay-toggle"
                 className={`h-8 px-2.5 inline-flex items-center gap-1 rounded-full text-[11px] font-bold transition ${
                   replay
                     ? "bg-gradient-to-r from-purple-500 to-indigo-600 text-white"
                     : "bg-white/10 hover:bg-white/20 text-white"
                 }`}>
          <ClockCounterClockwise size={12} weight={replay ? "fill" : "regular"}/>
          {replay ? "Live" : "Replay 24h"}
        </button>

        <button onClick={() => setTheme(t => t === "dark" ? "light" : "dark")}
                 data-testid="eye-theme-toggle"
                 title="Toggle light/dark map"
                 className="h-8 w-8 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-[11px]">
          {theme === "dark"
            ? <Sun size={13} weight="fill"/>
            : <Moon size={13} weight="fill"/>}
        </button>

        <button onClick={() => setReportOpen(true)}
                 data-testid="eye-report-open"
                 className="h-8 px-2.5 inline-flex items-center gap-1 rounded-full bg-white/10 hover:bg-white/20 text-[11px] font-bold">
          <FileText size={12} weight="fill"/>
          Report
        </button>

        {replay && (
          <button onClick={copyBookmark}
                   data-testid="eye-bookmark"
                   title="Copy shareable link to this moment"
                   className="h-8 w-8 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20">
            <LinkIcon size={13} weight="bold"/>
          </button>
        )}

        <button onClick={load}
                 data-testid="eye-refresh"
                 className="h-8 px-2.5 inline-flex items-center gap-1 rounded-full bg-white/10 hover:bg-white/20 text-[11px]">
          <ArrowsClockwise size={12} weight="bold" className={loading ? "animate-spin" : ""}/>
          Refresh
        </button>
      </div>

      {/* Partner sub-type chips (visible when Partner group is on) */}
      {visible.has("partner") && availablePartnerKinds.length > 0 && (
        <div className={`flex items-center gap-1.5 flex-wrap px-4 py-1.5 border-b ${theme === "dark" ? "bg-[#0F1A2E] border-white/10" : "bg-slate-50 border-slate-200"}`}
              data-testid="eye-partner-kinds">
          <span className={`text-[10px] uppercase tracking-widest font-bold ${theme === "dark" ? "text-white/50" : "text-slate-500"}`}>
            Partner types:
          </span>
          <button onClick={() => setPartnerKinds(new Set())}
                   data-testid="eye-partner-kind-all"
                   className={`h-6 px-2 rounded-full text-[10px] font-bold ${
                     partnerKinds.size === 0
                       ? "bg-emerald-500 text-white"
                       : (theme === "dark" ? "bg-white/10 text-white/70" : "bg-slate-200 text-slate-600")
                   }`}>ALL</button>
          {availablePartnerKinds.map(k => {
            const on = partnerKinds.has(k);
            return (
              <button key={k}
                       onClick={() => setPartnerKinds(prev => {
                         const n = new Set(prev);
                         n.has(k) ? n.delete(k) : n.add(k);
                         return n;
                       })}
                       data-testid={`eye-partner-kind-${k}`}
                       className={`h-6 px-2 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                         on
                           ? "bg-emerald-500 text-white"
                           : (theme === "dark" ? "bg-white/10 text-white/70 hover:bg-white/20" : "bg-slate-200 text-slate-700 hover:bg-slate-300")
                       }`}>{k}</button>
            );
          })}
        </div>
      )}

      {/* ═══ Replay slider (Iter 154/155) ═══════════════════════════ */}
      {!replay && (replayFrom || replayTo) && (
        <div className={`flex items-center gap-2 flex-wrap px-4 py-2 border-b ${theme === "dark" ? "bg-indigo-950/60 border-white/10" : "bg-indigo-50 border-slate-200"}`}
              data-testid="eye-replay-dates">
          <span className="text-[10px] uppercase tracking-widest font-bold opacity-70">
            Replay window:
          </span>
          <input type="datetime-local"
                  value={replayFrom} onChange={e => setReplayFrom(e.target.value)}
                  data-testid="eye-replay-from"
                  className={`h-7 px-2 rounded text-[11px] ${theme === "dark" ? "bg-white/10 text-white border border-white/20" : "bg-white text-slate-800 border border-slate-300"}`}/>
          <span className="opacity-60 text-[10px]">to</span>
          <input type="datetime-local"
                  value={replayTo} onChange={e => setReplayTo(e.target.value)}
                  data-testid="eye-replay-to"
                  className={`h-7 px-2 rounded text-[11px] ${theme === "dark" ? "bg-white/10 text-white border border-white/20" : "bg-white text-slate-800 border border-slate-300"}`}/>
          <button onClick={startReplay}
                   data-testid="eye-replay-apply"
                   className="h-7 px-3 rounded-full bg-indigo-500 text-white text-[11px] font-bold">
            Load
          </button>
          <button onClick={() => { setReplayFrom(""); setReplayTo(""); }}
                   className="h-7 px-2 rounded-full bg-white/10 text-[11px]">
            Cancel
          </button>
        </div>
      )}

      {replay && (
        <div className="flex items-center gap-3 px-4 py-2 bg-indigo-950/80 border-b border-white/10"
              data-testid="eye-replay-strip">
          <button
            onClick={() => setReplayPlaying(p => !p)}
            data-testid="eye-replay-play"
            className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shrink-0 hover:scale-105 transition">
            {replayPlaying
              ? <Pause size={14} weight="fill"/>
              : <Play size={14} weight="fill"/>}
          </button>
          <input
            type="range"
            min={0}
            max={Math.max(0, (replay.frames.length || 1) - 1)}
            value={replayIdx}
            onChange={(e) => { setReplayPlaying(false); setReplayIdx(Number(e.target.value)); }}
            data-testid="eye-replay-slider"
            className="flex-1 accent-purple-500"
          />
          <div className="text-[11px] font-mono text-white/85 tabular-nums shrink-0"
                data-testid="eye-replay-time">
            {replay.frames[replayIdx]?.t
              ? new Date(replay.frames[replayIdx].t).toLocaleString("en-IN", {
                  day: "numeric", month: "short", hour: "numeric", minute: "2-digit",
                })
              : "—"}
          </div>
          <div className="text-[10px] text-white/50">
            step {replay.step_minutes}m · frame {replayIdx + 1}/{replay.frames.length}
          </div>
        </div>
      )}

      {/* ═══ Map + optional side panel ══════════════════════════════ */}
      <div className="flex-1 flex overflow-hidden">
        <div className={selected ? "flex-1" : "w-full"} data-testid="eye-map-container">
          <MapContainer
            center={[center.lat, center.lng]}
            zoom={11}
            style={{ height: "100%", width: "100%", background: "#0F172A" }}
            scrollWheelZoom={true}
            attributionControl={false}
          >
            <TileLayer
              url={theme === "dark"
                ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png"
                : "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png"}
              subdomains="abcd"
            />
            {heatOn && (
              <HeatmapLayer
                points={filtered}
                weightFn={(p) => (
                  p.group === "ambulance" ? 1.0
                  : p.group === "field"    ? 0.9
                  : p.group === "customer" ? 0.75
                  : p.group === "office" || p.group === "gov" ? 0.6
                  : 0.4
                )}
              />
            )}
            {filtered.map(p => {
              const moving = lastPos[p.id]?.moved;
              const color = GROUP_META[p.group]?.color || "#EF4444";
              return (
                <Marker
                  key={p.id}
                  position={[p.lat, p.lng]}
                  icon={makeIcon(color, moving)}
                  eventHandlers={{ click: () => openActor(p) }}
                />
              );
            })}
            {selected && track.length >= 2 && (
              <Polyline
                positions={track.map(t => [t.lat, t.lng])}
                pathOptions={{
                  color: GROUP_META[selected.group]?.color || "#EF4444",
                  weight: 4, opacity: 0.75, dashArray: "8 6",
                }}
              />
            )}
            <FitBounds points={filtered}/>
          </MapContainer>
        </div>

        {selected && (
          <ActorPanel actor={selected} track={track}
                       onClose={() => { setSelected(null); setTrack([]); }}/>
        )}
      </div>

      {reportOpen && (
        <ReportModal theme={theme} onClose={() => setReportOpen(false)}/>
      )}
    </div>
  );
}


/* ─── Right-side actor detail panel ────────────────────────────── */
function ActorPanel({ actor, track, onClose }) {
  const g = GROUP_META[actor.group] || {};
  const Icon = g.Icon || User;
  const meta = actor.meta || {};
  return (
    <aside className="w-[320px] shrink-0 border-l border-white/10 bg-[#0B1220] flex flex-col"
            data-testid="eye-actor-panel">
      <div className="flex items-start gap-3 p-4 border-b border-white/10">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 shadow-md"
              style={{ background: g.color || "#334155" }}>
          <Icon size={20} weight="fill" color="white"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 font-bold">
            {g.label || actor.group}
          </div>
          <div className="text-[15px] font-semibold truncate" data-testid="eye-actor-name">
            {actor.name}
          </div>
          <div className="text-[11px] text-white/60 truncate">
            {actor.role}{actor.phone ? ` · ${actor.phone}` : ""}
          </div>
        </div>
        <button onClick={onClose}
                 data-testid="eye-actor-close"
                 className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">
          <X size={12}/>
        </button>
      </div>

      <div className="p-4 space-y-3 text-[12px] overflow-y-auto">
        <MetaRow label="Latitude"  value={actor.lat?.toFixed(5)}/>
        <MetaRow label="Longitude" value={actor.lng?.toFixed(5)}/>
        <MetaRow label="Updated"   value={actor.updated_at
          ? new Date(actor.updated_at).toLocaleString("en-IN") : "—"}/>
        {Object.entries(meta).map(([k, v]) => v !== null && v !== undefined && (
          <MetaRow key={k} label={k.replace(/_/g, " ")}
                    value={Array.isArray(v) ? v.join(", ") : String(v)}/>
        ))}
        <div className="pt-3 border-t border-white/10">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 font-bold mb-1.5">
            Recent breadcrumb ({track.length})
          </div>
          {track.length ? (
            <ul className="space-y-0.5 max-h-56 overflow-y-auto font-mono text-[10px] text-white/70">
              {track.slice(-10).reverse().map((t, i) => (
                <li key={i}>
                  {t.ts ? new Date(t.ts).toLocaleTimeString("en-IN") : "—"} ·
                  {" "}{t.lat?.toFixed(4)}, {t.lng?.toFixed(4)}
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-[11px] text-white/40 italic">No history yet</div>
          )}
        </div>
      </div>
    </aside>
  );
}
function MetaRow({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-[10px] uppercase tracking-[0.18em] text-white/50 font-bold">{label}</span>
      <span className="font-mono text-[11px] text-white/85 text-right truncate max-w-[62%]">{value}</span>
    </div>
  );
}


// ─── Daily activity report modal ────────────────────────────────
function ReportModal({ theme, onClose }) {
  const [group, setGroup] = useState("field");
  const [date, setDate]   = useState(() => new Date().toISOString().slice(0, 10));
  const [actors, setActors]   = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [busy, setBusy]   = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    setBusy(true); setSelectedIds(new Set());
    api.get("/admin/eagleeye/actors", { params: { group } })
      .then(r => setActors(r.data.actors || []))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setBusy(false));
  }, [group]);

  const generate = async () => {
    setDownloading(true);
    try {
      const params = { group, date, fmt: "csv" };
      if (selectedIds.size > 0) params.actor_ids = Array.from(selectedIds).join(",");
      const r = await api.get("/admin/eagleeye/report", { params, responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `eagleeye_${group}_${date}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDownloading(false); }
  };

  const dark = theme === "dark";
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={onClose} data-testid="eye-report-modal">
      <div onClick={e => e.stopPropagation()}
            className={`w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden ${dark ? "bg-[#0B1220] text-white" : "bg-white text-slate-900"}`}>
        <div className={`flex items-center justify-between p-4 border-b ${dark ? "border-white/10" : "border-slate-200"}`}>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] opacity-60 font-bold">Daily report</div>
            <div className="font-heading text-lg">Activity export</div>
          </div>
          <button onClick={onClose} className={`w-7 h-7 rounded-full flex items-center justify-center ${dark ? "bg-white/10" : "bg-slate-100"}`}>
            <X size={12}/>
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold opacity-70">User type</label>
              <select value={group} onChange={e => setGroup(e.target.value)}
                       data-testid="eye-report-group"
                       className={`w-full h-9 px-2 rounded text-sm mt-1 ${dark ? "bg-white/10 border border-white/15" : "bg-white border border-slate-300"}`}>
                <option value="field">Field staff</option>
                <option value="office">Office staff</option>
                <option value="gov">Gov users</option>
                <option value="customer">Customers</option>
                <option value="partner">Service partners</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold opacity-70">Date</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)}
                      data-testid="eye-report-date"
                      className={`w-full h-9 px-2 rounded text-sm mt-1 ${dark ? "bg-white/10 border border-white/15" : "bg-white border border-slate-300"}`}/>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold opacity-70">
              Users ({selectedIds.size ? `${selectedIds.size} selected` : "leave empty for all"})
            </label>
            <div className={`max-h-56 overflow-y-auto rounded mt-1 divide-y ${dark ? "bg-white/5 divide-white/10" : "bg-slate-50 divide-slate-200"}`}
                  data-testid="eye-report-actors">
              {busy && <div className="p-3 text-xs opacity-60">Loading…</div>}
              {!busy && actors.length === 0 && (
                <div className="p-3 text-xs opacity-60">No users found for this group.</div>
              )}
              {actors.map(a => {
                const on = selectedIds.has(a.id);
                return (
                  <label key={a.id}
                          className={`flex items-center gap-2 p-2 cursor-pointer text-sm ${on ? (dark ? "bg-emerald-900/25" : "bg-emerald-50") : ""}`}>
                    <input type="checkbox" checked={on}
                            onChange={() => setSelectedIds(prev => {
                              const n = new Set(prev);
                              n.has(a.id) ? n.delete(a.id) : n.add(a.id);
                              return n;
                            })}
                            data-testid={`eye-report-actor-${a.id}`}
                            className="accent-emerald-500"/>
                    <div className="flex-1 min-w-0">
                      <div className="truncate font-semibold text-[13px]">{a.name}</div>
                      {a.sub && <div className="text-[10px] opacity-60 truncate">{a.sub}</div>}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="text-[11px] opacity-60 italic">
            CSV includes: timestamp, lat/lng, cumulative distance travelled, and ping source.
          </div>
        </div>

        <div className={`p-4 border-t flex justify-end gap-2 ${dark ? "border-white/10" : "border-slate-200"}`}>
          <button onClick={onClose}
                   className={`h-9 px-4 rounded text-sm ${dark ? "bg-white/10" : "bg-slate-200"}`}>
            Cancel
          </button>
          <button onClick={generate} disabled={downloading}
                   data-testid="eye-report-generate"
                   className="h-9 px-4 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold disabled:opacity-60">
            {downloading ? "Generating…" : "Download CSV"}
          </button>
        </div>
      </div>
    </div>
  );
}
